import { TOAST_TIMEOUT } from 'src/config/Toast';
import store from 'src/store';
import {
  type BuildingType,
  type Country,
  type Org,
  type SubOrg,
  type TableDataRow,
} from 'src/store/adminConfigPageSlice/siteConfigPageSlice/types';
import { type Site as AllowedSite } from 'src/store/siteSlice';
import { createToast } from 'src/store/toastsSlice';
import { type ApplicableJob, type SiteCapacityUpdateResponse } from 'src/store/updateSiteCapacitySlice/types';
import apiClient from 'src/utils/apiClient';

import { type EmailSettings } from '../emailDistribution/types/emailSettings';
import { type Region } from '../tasks/types';

import { type Site, type SiteCapacityPayload, type SitePayload } from './types';

class SitesService {
  /**
   * get all sites
   */
  public async getSites() {
    return await apiClient.get<Site[]>('/sites');
  }

  /**
   * get all sites
   */
  public async getAllowedSites(axiosClient = apiClient) {
    const res = await axiosClient.get<AllowedSite[]>('/sites/allowed');
    return res.data;
  }

  /**
   * Get email settings of a site
   * @param siteId site id for which email settings need to be fetched
   * @returns email settings of site
   */
  public async getEmailsForSite(siteId: number, axiosClient = apiClient) {
    const res = await axiosClient.get<EmailSettings>(`/sites/${siteId}/emailSetting`);
    return res.data;
  }

  /**
   * get countries
   */
  public async getCountries() {
    return await apiClient.get<Country[]>('/countries');
  }

  /**
   * get regions
   */
  public async getRegions(axiosClient = apiClient) {
    const res = await axiosClient.get<Region[]>('/regions');
    return res.data;
  }

  /**
   * get orgs
   */
  public async getOrgs() {
    return await apiClient.get<Org[]>('/organizations');
  }

  /**
   * get orgs
   */
  public async getSubOrgs() {
    return await apiClient.get<SubOrg[]>('/suborganizations');
  }

  /**
   * get buildingTypes
   */
  public async getBuildingTypes() {
    return await apiClient.get<BuildingType[]>('/buildingTypes');
  }

  /**
   * Create or update site
   */
  public async createOrUpdateSite(data: SitePayload, selectedSite?: TableDataRow) {
    let res = null;
    if (selectedSite) {
      res = await apiClient.put<Site>(`/sites/${selectedSite.id}`, data);
    } else {
      res = await apiClient.post<Site>('/sites', data);
    }
    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: selectedSite ? 'Updated site successfully' : 'Created site successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * get applicable jobs for a site
   */
  public async getApplicableJobs(siteId: number) {
    return await apiClient.get<ApplicableJob[]>(`/sites/${siteId}/applicableJobs`);
  }

  /**
   * Create or update site capacity
   */
  public async createOrUpdateSiteCapacity(data: SiteCapacityPayload) {
    const res = await apiClient.post<SiteCapacityUpdateResponse>(`/sites/jobs`, data);

    if (res) {
      store.dispatch(
        createToast({
          type: 'success',
          message: 'Updated site capacity successfully',
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
    return res;
  }

  /**
   * get region specific sites
   */
  public async getRegionSites(regionId: number) {
    return await apiClient.get<Site[]>(`/sites/region/${regionId}`);
  }

  /**
   * get all active sites
   */
  public async getActiveSites() {
    return await apiClient.get<Site[]>(`/sites/active`);
  }

  /**
   * get site by id
   */
  public async getSiteById(siteId: number) {
    return await apiClient.get<Site>(`/sites/${siteId}`);
  }
}

export default new SitesService();
